import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Page, Layout, Card, FormLayout, TextField, Select, Button, Banner,
  Spinner, Box, Text, InlineStack, BlockStack, Divider, Checkbox,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

const FONT_OPTIONS = [
  { label: "Inter (Modern)", value: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { label: "Playfair Display (Elegant)", value: "'Playfair Display', Georgia, serif" },
  { label: "Poppins (Friendly)", value: "Poppins, -apple-system, sans-serif" },
  { label: "Montserrat (Clean)", value: "Montserrat, -apple-system, sans-serif" },
  { label: "System Default", value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
];

const BUTTON_STYLES = [
  { label: "Gradient", value: "gradient" },
  { label: "Solid", value: "solid" },
  { label: "Outline", value: "outline" },
];

function ColorInput({ label, value, onChange }) {
  return (
    <div>
      <Text variant="bodyMd" fontWeight="semibold">{label}</Text>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
        <input
          type="color"
          value={value || "#6366f1"}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 40, height: 40, border: "1px solid #ddd", borderRadius: 8, cursor: "pointer", padding: 2 }}
        />
        <TextField
          label=""
          labelHidden
          value={value || ""}
          onChange={onChange}
          autoComplete="off"
          monospaced
        />
      </div>
    </div>
  );
}

function PreviewCard({ branding, t }) {
  const bg = branding.backgroundColor || "#f8f7ff";
  const primary = branding.primaryColor || "#6366f1";
  const secondary = branding.secondaryColor || "#8b5cf6";
  const accent = branding.accentColor || "#10b981";
  const text = branding.textColor || "#1a1a1a";
  const font = branding.fontFamily || "Inter, sans-serif";
  const radius = parseInt(branding.borderRadius || "20");
  const btnStyle = branding.buttonStyle || "gradient";

  const btnCss = btnStyle === "gradient"
    ? { background: `linear-gradient(135deg, ${primary}, ${secondary})`, color: "#fff", border: "none" }
    : btnStyle === "solid"
    ? { background: primary, color: "#fff", border: "none" }
    : { background: "transparent", color: primary, border: `2px solid ${primary}` };

  return (
    <div style={{ background: bg, borderRadius: 16, padding: 20, fontFamily: font, color: text }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        {branding.logoUrl && (
          <img src={branding.logoUrl} alt="" style={{ maxHeight: 40, marginBottom: 8 }} />
        )}
        <h3 style={{ fontWeight: 700, fontSize: 18, margin: 0, fontFamily: font }}>
          {branding.storeName || t("branding.previewStoreName")}
        </h3>
        <p style={{ fontSize: 13, opacity: 0.6, margin: "4px 0 0" }}>{t("branding.previewSubtitle")}</p>
      </div>
      <div style={{ background: "#fff", borderRadius: radius, padding: 16, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.5, marginBottom: 8 }}>
          {t("branding.previewResult")}
        </div>
        <div style={{ background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`, borderRadius: radius * 0.6, height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 32 }}>👗</span>
        </div>
      </div>
      <button style={{
        display: "block", width: "100%", padding: "14px", fontSize: 15, fontWeight: 700,
        borderRadius: radius, cursor: "pointer", textAlign: "center", fontFamily: font,
        ...btnCss,
      }}>
        🛒 {t("branding.previewButton")}
      </button>
    </div>
  );
}

export default function BrandingPage() {
  const { apiFetch, shopDomain } = useShop();
  const { t } = useI18n();
  const RADIUS_OPTIONS = useMemo(() => [
    { label: `${t("branding.radiusSharp")} (0px)`, value: "0" },
    { label: `${t("branding.radiusSlight")} (8px)`, value: "8" },
    { label: `${t("branding.radiusRounded")} (16px)`, value: "16" },
    { label: `${t("branding.radiusRound")} (20px)`, value: "20" },
    { label: `${t("branding.radiusPill")} (100px)`, value: "100" },
  ], [t]);
  const [branding, setBranding] = useState({
    logoUrl: "", storeName: "", primaryColor: "#6366f1", secondaryColor: "#8b5cf6",
    accentColor: "#10b981", backgroundColor: "#f8f7ff", textColor: "#1a1a1a",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    borderRadius: "20", buttonStyle: "gradient", showImageBadge: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch("/branding")
      .then((data) => setBranding((prev) => ({ ...prev, ...data })))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [shopDomain]);

  const update = (field) => (val) => setBranding((prev) => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await apiFetch("/branding", {
        method: "PUT",
        body: JSON.stringify(branding),
      });
      setBranding((prev) => ({ ...prev, ...data }));
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Page title={t("branding.title")}><Box padding="800"><Spinner /></Box></Page>;
  }

  return (
    <Page
      title={t("branding.title")}
      subtitle={t("branding.subtitle")}
      primaryAction={{ content: t("common.save"), onAction: handleSave, loading: saving }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
          </Layout.Section>
        )}
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(false)}>{t("branding.saved")}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd">{t("branding.storeDetails")}</Text>
                <FormLayout>
                  <TextField
                    label={t("branding.storeName")}
                    value={branding.storeName || ""}
                    onChange={update("storeName")}
                    placeholder={t("branding.storeNamePlaceholder")}
                    autoComplete="off"
                  />
                  <TextField
                    label={t("branding.logoUrl")}
                    value={branding.logoUrl || ""}
                    onChange={update("logoUrl")}
                    placeholder="https://jouwwinkel.nl/logo.png"
                    helpText={t("branding.logoHelp")}
                    autoComplete="off"
                  />
                </FormLayout>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd">{t("branding.colors")}</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <ColorInput label={t("branding.primaryColor")} value={branding.primaryColor} onChange={update("primaryColor")} />
                    <ColorInput label={t("branding.secondaryColor")} value={branding.secondaryColor} onChange={update("secondaryColor")} />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <ColorInput label={t("branding.accentColor")} value={branding.accentColor} onChange={update("accentColor")} />
                    <ColorInput label={t("branding.backgroundColor")} value={branding.backgroundColor} onChange={update("backgroundColor")} />
                  </FormLayout.Group>
                  <ColorInput label={t("branding.textColor")} value={branding.textColor} onChange={update("textColor")} />
                </FormLayout>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd">{t("branding.style")}</Text>
                <FormLayout>
                  <Select
                    label={t("branding.fontFamily")}
                    options={FONT_OPTIONS}
                    value={branding.fontFamily || FONT_OPTIONS[0].value}
                    onChange={update("fontFamily")}
                  />
                  <Select
                    label={t("branding.borderRadius")}
                    options={RADIUS_OPTIONS}
                    value={branding.borderRadius || "20"}
                    onChange={update("borderRadius")}
                  />
                  <Select
                    label={t("branding.buttonStyle")}
                    options={BUTTON_STYLES}
                    value={branding.buttonStyle || "gradient"}
                    onChange={update("buttonStyle")}
                  />
                  <Checkbox
                    label={t("branding.showImageBadge")}
                    helpText={t("branding.showImageBadgeHelp")}
                    checked={!!branding.showImageBadge}
                    onChange={update("showImageBadge")}
                  />
                </FormLayout>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingMd">{t("branding.preview")}</Text>
                <Text variant="bodySm" tone="subdued">{t("branding.previewDesc")}</Text>
                <PreviewCard branding={branding} t={t} />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
